import { useAuth } from '@/auth/auth-provider'
import { isSuper, parsePrincipalFromUser } from '@/auth/principal'
import dealerLogoPlaceholder from '@/assets/images/dealer-logo-placeholder.svg'
import { useDealership } from '@/contexts/DealershipContext'
import { getGroupConfiguration } from '@/services/configuration.api'
import { DealershipEntity, DealershipGroup, NavItem } from '@/types'
import { AppShell, Avatar, Breadcrumbs, Group, Menu, Tabs, Text, UnstyledButton, rem, useMantineColorScheme } from '@mantine/core'
import {
  IconBuilding,
  IconCalendar,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconDashboard,
  IconDeviceDesktop,
  IconLogout,
  IconMessageCircle,
  IconMoon,
  IconPalette,
  IconSettings,
  IconShieldBolt,
  IconSun,
  IconUser,
} from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

// Define navigation items per type
const groupNavItems: NavItem[] = [
  { path: '/group/dashboard', label: 'Dashboard', icon: IconDashboard, badge: null, visibleFor: ['group'] },
  { path: '/group/dealers', label: 'Dealers', icon: IconBuilding, badge: null, visibleFor: ['group'] },
  { path: '/group/configuration', label: 'Configuration', icon: IconSettings, badge: null, visibleFor: ['group'] },
]

const dealershipNavItems: NavItem[] = [
  { path: '/dealership/conversations', label: 'Conversations', icon: IconMessageCircle, badge: null, visibleFor: ['dealership'] },
  { path: '/dealership/appointments', label: 'Appointments', icon: IconCalendar, badge: null, visibleFor: ['dealership'] },
  { path: '/dealership/configuration', label: 'Configuration', icon: IconSettings, badge: null, visibleFor: ['dealership'] },
]

const iconSm = { width: rem(14), height: rem(14) }

export function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedEntity, setSelectedEntity, entities, currentType, getDealershipsByGroup, getParentGroup } = useDealership()
  const { user, signOut } = useAuth()
  const { colorScheme, setColorScheme } = useMantineColorScheme()

  const principal = useMemo(() => parsePrincipalFromUser(user), [user])
  const isSuperUser = useMemo(() => isSuper(principal), [principal])
  const [groupLogoUrl, setGroupLogoUrl] = useState<string | null>(null)
  const [groupPrimaryColor, setGroupPrimaryColor] = useState<string | null>(null)

  // Fetch group branding when entity changes
  useEffect(() => {
    async function fetchGroupBranding() {
      try {
        let groupId: string | null = null

        if (selectedEntity) {
          if (selectedEntity.type === 'group') {
            groupId = selectedEntity.id
          } else {
            // For dealership, get parent group
            const parentGroup = getParentGroup(selectedEntity.id)
            groupId = parentGroup?.id || null
          }
        }

        if (groupId) {
          const response = await getGroupConfiguration<any>(groupId)
          if (response.success && response.data?.brandingGuidelines) {
            const branding = response.data.brandingGuidelines
            setGroupLogoUrl(branding.logoUrl || null)
            setGroupPrimaryColor(branding.primaryColor || null)
          } else {
            setGroupLogoUrl(null)
            setGroupPrimaryColor(null)
          }
        } else {
          setGroupLogoUrl(null)
          setGroupPrimaryColor(null)
        }
      } catch (error) {
        console.error('Failed to fetch group branding:', error)
        setGroupLogoUrl(null)
        setGroupPrimaryColor(null)
      }
    }

    fetchGroupBranding()
  }, [selectedEntity, getParentGroup])

  // Determine if we should use light text based on background color
  const shouldUseLightText = useMemo(() => {
    if (!groupPrimaryColor) return false

    // Convert hex to RGB and calculate luminance
    const hex = groupPrimaryColor.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

    // Use light text if background is dark (luminance < 0.5)
    return luminance < 0.5
  }, [groupPrimaryColor])

  // Get contextual navigation based on selected entity type
  const currentNavItems = useMemo(() => {
    const isGroupShell = currentType === 'group' || currentType === null
    let raw = isGroupShell ? groupNavItems : dealershipNavItems
    return raw
  }, [currentType, principal, isSuperUser])

  /** Scoped groups from context (already filtered for non-super). */
  const scopedGroups = useMemo(
    () => entities.filter((e): e is DealershipGroup => e.type === 'group'),
    [entities],
  )
  const showGroupSwitcher = scopedGroups.length > 1

  // Handle entity selection (from breadcrumb dropdown)
  const handleEntitySelect = (entity: DealershipEntity) => {
    setSelectedEntity(entity)

    // Navigate to appropriate default page
    if (entity.type === 'group') {
      navigate('/group/dashboard')
    } else {
      navigate('/dealership/conversations')
    }
  }

  // Get current page name from contextual navigation
  const getCurrentPageName = () => {
    const path = location.pathname
    const currentItem = currentNavItems.find(item => path.startsWith(item.path))
    return currentItem ? currentItem.label : 'Dashboard'
  }

  // Build breadcrumb with hierarchy
  const breadcrumbItems = []
  const textColor = shouldUseLightText ? 'white' : undefined

  if (selectedEntity) {
    if (selectedEntity.type === 'dealership') {
      // Show: Group > Dealership > Page
      const parentGroup = getParentGroup(selectedEntity.id)
      const dealersInParentGroup = parentGroup ? getDealershipsByGroup(parentGroup.id) : []
      const showDealerSwitcher = dealersInParentGroup.length > 1

      if (parentGroup) {
        if (showGroupSwitcher) {
          breadcrumbItems.push(
            <Menu key="group" position="bottom-start">
              <Menu.Target>
                <UnstyledButton>
                  <Group gap={4} align="center">
                    <IconBuilding size={18} color={textColor} />
                    <Text size="md" fw={500} c={textColor}>
                      {parentGroup.name}
                    </Text>
                    <IconChevronDown size={16} color={textColor} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                {scopedGroups.map(group => (
                  <Menu.Item key={group.id} onClick={() => handleEntitySelect(group)}>
                    {group.name}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>,
          )
        } else {
          breadcrumbItems.push(
            <UnstyledButton key="group" onClick={() => handleEntitySelect(parentGroup)}>
              <Group gap={4} align="center">
                <IconBuilding size={18} color={textColor} />
                <Text size="md" fw={500} c={textColor}>
                  {parentGroup.name}
                </Text>
              </Group>
            </UnstyledButton>,
          )
        }
      }

      if (showDealerSwitcher) {
        breadcrumbItems.push(
          <Menu key="dealership" position="bottom-start">
            <Menu.Target>
              <UnstyledButton>
                <Group gap={4} align="center">
                  <Text size="md" fw={500} c={textColor}>
                    {selectedEntity.name}
                  </Text>
                  <IconChevronDown size={16} color={textColor} />
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              {dealersInParentGroup.map(dealer => (
                <Menu.Item
                  key={dealer.id}
                  onClick={() => handleEntitySelect(dealer)}
                  rightSection={dealer.id === selectedEntity.id ? '✓' : null}
                >
                  {dealer.name}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>,
        )
      } else {
        breadcrumbItems.push(
          <Group key="dealership" gap={4} align="center">
            <Text size="md" fw={500} c={textColor}>
              {selectedEntity.name}
            </Text>
          </Group>,
        )
      }
    } else {
      // Group level: Show Group > Dealerships (quick nav) > Page
      const group = selectedEntity as DealershipGroup
      const showDealerQuickNav = group.dealerships.length > 1

      if (showGroupSwitcher) {
        breadcrumbItems.push(
          <Menu key="group" position="bottom-start">
            <Menu.Target>
              <UnstyledButton>
                <Group gap={4} align="center">
                  <IconBuilding size={18} color={textColor} />
                  <Text size="md" fw={500} c={textColor}>
                    {selectedEntity.name}
                  </Text>
                  <IconChevronDown size={16} color={textColor} />
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              {scopedGroups.map(g => (
                <Menu.Item
                  key={g.id}
                  onClick={() => handleEntitySelect(g)}
                  rightSection={g.id === selectedEntity.id ? '✓' : null}
                >
                  {g.name}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>,
        )
      } else {
        breadcrumbItems.push(
          <Group key="group" gap={4} align="center">
            <IconBuilding size={18} color={textColor} />
            <Text size="md" fw={500} c={textColor}>
              {selectedEntity.name}
            </Text>
          </Group>,
        )
      }

      if (showDealerQuickNav) {
        breadcrumbItems.push(
          <Menu key="dealerships-quick-nav" position="bottom-start">
            <Menu.Target>
              <UnstyledButton>
                <Group gap={4} align="center">
                  <Text size="md" fw={500} c={shouldUseLightText ? 'gray.3' : 'dimmed'}>
                    All Dealerships
                  </Text>
                  <IconChevronDown size={16} color={textColor} opacity={0.6} />
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Switch dealership</Menu.Label>
              {group.dealerships.map(dealer => (
                <Menu.Item key={dealer.id} onClick={() => handleEntitySelect(dealer)} leftSection={<IconBuilding size={14} />}>
                  <div>
                    <Text size="sm">{dealer.name}</Text>
                    <Text size="xs" c="dimmed">
                      {dealer.location}
                    </Text>
                  </div>
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>,
        )
      }
    }

    // Current page name
    breadcrumbItems.push(
      <Text key="page" size="md" c={shouldUseLightText ? 'gray.3' : 'dimmed'}>
        {getCurrentPageName()}
      </Text>,
    )
  }

  return (
    <AppShell header={{ height: { base: 110, sm: 110 } }} padding="md">
      {/* Primary Header */}
      <AppShell.Header
        style={{
          backgroundColor: groupPrimaryColor || undefined,
        }}
      >
        <Group h={60} px="md" justify="space-between" align="center" data-group="true">
          <Group gap="md" align="center">
            {/* Logo */}
            <UnstyledButton
              onClick={() => navigate(currentType === 'group' ? '/group/dashboard' : '/dealership/conversations')}
              title="Go to Dashboard"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={groupLogoUrl || dealerLogoPlaceholder}
                alt="Logo"
                style={{
                  height: '40px',
                  maxWidth: '120px',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </UnstyledButton>

            {/* Breadcrumb Navigation */}
            <Breadcrumbs
              separator="/"
              separatorMargin="sm"
              styles={{
                separator: {
                  color: shouldUseLightText ? 'white' : undefined,
                },
              }}
            >
              {breadcrumbItems}
            </Breadcrumbs>
          </Group>

          {/* Right side - User Menu */}
          <Group gap="sm" align="center">
            {/* User Menu */}
            <Menu shadow="md" width={240}>
              <Menu.Target>
                <UnstyledButton>
                  <Group gap="xs" align="center">
                    <Avatar size="md" alt={user?.name || 'User'}>
                      {user?.name?.charAt(0) || 'U'}
                    </Avatar>
                    <Group gap="xs" align="center">
                      <Text size="md" fw={500} visibleFrom="sm" c={textColor}>
                        {user?.name || 'User'}
                      </Text>
                      {isSuperUser && <IconShieldBolt size={16} color="#10b981" />}
                    </Group>
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Account</Menu.Label>
                <Menu.Item leftSection={<IconUser style={iconSm} />} onClick={() => navigate('/profile')}>
                  Profile
                </Menu.Item>
                {isSuperUser && (
                  <Menu.Item leftSection={<IconShieldBolt style={iconSm} />} onClick={() => navigate('/admin/system')}>
                    System Administration
                  </Menu.Item>
                )}

                <Menu.Divider />

                <Menu
                  width={200}
                  position="right-start"
                  trigger="hover"
                  openDelay={100}
                  closeDelay={200}
                  offset={2}
                  withinPortal={false}
                >
                  <Menu.Target>
                    <Menu.Item
                      component="div"
                      leftSection={<IconPalette style={iconSm} />}
                      rightSection={<IconChevronRight size={14} stroke={1.5} />}
                    >
                      Appearance
                    </Menu.Item>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconSun style={iconSm} />}
                      rightSection={colorScheme === 'light' ? <IconCheck size={14} /> : null}
                      onClick={() => setColorScheme('light')}
                    >
                      Light
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconMoon style={iconSm} />}
                      rightSection={colorScheme === 'dark' ? <IconCheck size={14} /> : null}
                      onClick={() => setColorScheme('dark')}
                    >
                      Dark
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconDeviceDesktop style={iconSm} />}
                      rightSection={colorScheme === 'auto' ? <IconCheck size={14} /> : null}
                      onClick={() => setColorScheme('auto')}
                    >
                      System
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>

                <Menu.Divider />

                <Menu.Item
                  color="red"
                  leftSection={<IconLogout style={iconSm} />}
                  onClick={() =>
                    signOut()
                  }
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>

        {/* Secondary Navigation Bar */}
        <Tabs value={location.pathname} onChange={value => value && navigate(value)} h={50} px="md">
          <Tabs.List>
            {currentNavItems.map(item => {
              const Icon = item.icon
              return (
                <Tabs.Tab key={item.path} value={item.path} leftSection={<Icon size={20} stroke={1.5} />}>
                  {item.label}
                </Tabs.Tab>
              )
            })}
          </Tabs.List>
        </Tabs>
      </AppShell.Header>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  )
}
